/**
 * RiskScoreCard — overall severity-weighted risk score 0..10 with a
 * half-doughnut SVG gauge. Color scales from green → red via
 * `getRiskScoreColor`. Total risks in the header meta.
 *
 * Pure presentation; consumes `data.riskScore` + `data.riskDistribution`.
 */
import React from 'react';
import { getRiskScoreColor } from '../../../../utils/severityStyle';
import {
  TypographyH2,
  TypographyMuted,
} from '../../../../components/ui/Typography';
import type { AggregatedOverviewData } from '../../aggregate/types';
import styles from './RiskScoreCard.module.css';

interface RiskScoreCardProps {
  data: AggregatedOverviewData;
}

const MAX = 10;
const RADIUS = 70;
const CX = 100;
const CY = 90;

export default function RiskScoreCard({ data }: RiskScoreCardProps) {
  const score = data.riskScore;
  const dist = data.riskDistribution;
  const total = dist.critical + dist.high + dist.medium + dist.low;

  if (total === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>Overall Risk Score</TypographyH2>
        </div>
        <div className={styles.gaugeWrap}>
          <svg className={styles.gaugeSvg} viewBox="0 0 200 110" aria-hidden="true">
            <path
              d={`M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`}
              className={styles.gaugeTrack}
              strokeWidth="14"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
          <div className={`${styles.gaugeValue} ${styles.gaugeValueEmpty}`}>—</div>
          <div className={styles.gaugeMax}>no risks yet</div>
        </div>
      </div>
    );
  }

  const circumference = Math.PI * RADIUS;
  const pct = Math.min(1, Math.max(0, score / MAX));
  const dash = pct * circumference;
  const color = getRiskScoreColor(score);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Overall Risk Score</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>
          {total} risk{total === 1 ? '' : 's'}
        </TypographyMuted>
      </div>
      <div className={styles.gaugeWrap}>
        <svg className={styles.gaugeSvg} viewBox="0 0 200 110" aria-hidden="true">
          <path
            d={`M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`}
            className={styles.gaugeTrack}
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d={`M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`}
            stroke={color}
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
          />
        </svg>
        <div className={styles.gaugeValue} style={{ color }}>
          {score.toFixed(1)}
        </div>
        <div className={styles.gaugeMax}>/ {MAX}</div>
      </div>
    </div>
  );
}
